// Ingest keys (screen 4; managers only): metadata from the profile (no extra call), create and
// revoke. The plaintext (`cosk_in_…`) comes back once, in the create response, and lives only
// in this dialog's state: it cannot be dismissed (Escape, outside click, close button) until
// the user ticks "I've copied it", and it is dropped from memory when the dialog closes.

import { ApiError, newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
  Input,
  Label,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  workerName,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CopyIcon, KeyRoundIcon } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { AgentKey, AgentKeyCreated, AgentKeyScope, DeProfile } from "../../api/types";
import { formatDateTime } from "../format";
import { keys as queryKeys } from "../queries";
import { Nothing } from "../screen-parts";

const SCOPES: { scope: AgentKeyScope; label: string }[] = [
  { scope: "ingest:activity", label: "Activity events" },
  { scope: "ingest:outcome", label: "Outcome events" },
  { scope: "ingest:cost", label: "Cost items" },
];

export function KeysTab({ api, companyId, de }: { api: Api; companyId: string; de: DeProfile }) {
  const queryClient = useQueryClient();
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.de(companyId, de.membership_id) });
  const active = de.keys.filter((k) => k.status === "active");
  const revoked = de.keys.filter((k) => k.status !== "active");

  return (
    <div className="flex min-w-0 flex-col gap-4" data-slot="de-keys">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Keys let {workerName(de.display_name)}'s runtime post events for this DE only.
        </p>
        <CreateKeyDialog
          key={`${companyId}:${de.membership_id}`}
          api={api}
          companyId={companyId}
          de={de}
          onCreated={refresh}
        />
      </div>
      {de.keys.length ? (
        <Table>
          <TableCaption>Ingest keys. Only the prefix is stored and shown.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Prefix</TableHead>
              <TableHead scope="col">Scopes</TableHead>
              <TableHead scope="col">Status</TableHead>
              <TableHead scope="col">Last used</TableHead>
              <TableHead scope="col">Created</TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...active, ...revoked].map((k) => (
              <TableRow key={k.id} data-key={k.id}>
                <TableHead scope="row" className="font-mono text-xs font-normal">
                  {k.key_prefix}…
                </TableHead>
                <TableCell>
                  <span className="flex flex-wrap gap-1">
                    {k.scopes.map((s) => (
                      <Badge key={s} variant="outline">
                        {s.replace("ingest:", "")}
                      </Badge>
                    ))}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge variant={k.status === "active" ? "secondary" : "outline"}>
                    {k.status === "active" ? "Active" : "Revoked"}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {k.last_used_at ? formatDateTime(k.last_used_at) : "Never"}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDateTime(k.created_at)}
                </TableCell>
                <TableCell className="text-right">
                  {k.status === "active" ? (
                    <RevokeKeyDialog
                      api={api}
                      companyId={companyId}
                      de={de}
                      agentKey={k}
                      onRevoked={refresh}
                    />
                  ) : k.revoked_at ? (
                    <span className="text-xs text-muted-foreground">
                      Revoked {formatDateTime(k.revoked_at)}
                    </span>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Nothing title="No keys yet">Create a key to connect the DE's runtime.</Nothing>
      )}
    </div>
  );
}

function problemText(error: unknown, fallback: string): string {
  return error instanceof ApiError
    ? (error.problem?.detail ?? error.problem?.title ?? fallback)
    : fallback;
}

export function CreateKeyDialog({
  api,
  companyId,
  de,
  onCreated,
}: {
  api: Api;
  companyId: string;
  /** CONNECTIONS-AREA: a DE's summary row is enough (Team › Connections › Agent keys). */
  de: Pick<DeProfile, "membership_id" | "display_name" | "seat">;
  onCreated: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [scopes, setScopes] = useState<AgentKeyScope[]>(SCOPES.map((s) => s.scope));
  const [created, setCreated] = useState<AgentKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "done" | "failed">("idle");
  const plaintextId = useId();
  const confirmId = useId();

  // Each opening of the dialog is a session; a response that lands after its session ended
  // (closed or unmounted) is discarded, so a late plaintext is never stored anywhere.
  const session = useRef(0);
  useEffect(
    () => () => {
      session.current += 1;
    },
    [],
  );

  const create = useMutation({
    // The plaintext goes straight into dialog-local state; the mutation (and so TanStack's
    // MutationCache) only ever holds the non-secret key metadata.
    mutationFn: async (): Promise<AgentKeyCreated["key"]> => {
      const mine = session.current;
      const result = await api.client.digitalEmployees.createKey({
        params: { co: companyId, id: de.membership_id },
        idempotencyKey: newIdempotencyKey(),
        body: { scopes },
      });
      if (mine === session.current) setCreated({ key: result.key, plaintext: result.plaintext });
      return result.key;
    },
    onSuccess: () => onCreated(),
  });

  const reset = () => {
    session.current += 1;
    setCreated(null); // the plaintext leaves memory with the dialog
    setCopied(false);
    setCopyState("idle");
    setScopes(SCOPES.map((s) => s.scope));
    create.reset();
  };
  // While a create is in flight nothing closes the dialog; while a plaintext is shown, only
  // the explicit "Done" (after "I've copied it") does.
  const onOpenChange = (next: boolean) => {
    if (!next && create.isPending) return;
    if (!next && created && !copied) return;
    setOpen(next);
    if (!next) reset();
  };
  const copy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.plaintext);
      setCopyState("done");
    } catch {
      setCopyState("failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Button className="max-md:h-11" onClick={() => setOpen(true)}>
        <KeyRoundIcon aria-hidden="true" />
        Create key
      </Button>
      <DialogContent showCloseButton={!created && !create.isPending} className="sm:max-w-lg">
        {created ? (
          <>
            <DialogHeader>
              <DialogTitle>Copy the new key</DialogTitle>
              <DialogDescription>
                This is the only time the key is shown. Store it in {workerName(de.display_name)}'s
                runtime secrets now; CompanyOS keeps only the prefix {created.key.prefix}.
              </DialogDescription>
            </DialogHeader>
            <Field>
              <FieldLabel htmlFor={plaintextId}>Ingest key</FieldLabel>
              <div className="flex min-w-0 gap-2">
                <Input
                  id={plaintextId}
                  readOnly
                  value={created.plaintext}
                  className="min-w-0 font-mono text-xs"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button variant="outline" onClick={() => void copy()} className="max-md:h-11">
                  <CopyIcon aria-hidden="true" />
                  Copy
                </Button>
              </div>
              <FieldDescription aria-live="polite">
                {copyState === "done"
                  ? "Copied to the clipboard."
                  : copyState === "failed"
                    ? "Copy failed: select the key and copy it by hand."
                    : `Scopes: ${created.key.scopes.join(", ")}`}
              </FieldDescription>
            </Field>
            <div className="flex items-center gap-2">
              <Checkbox
                id={confirmId}
                checked={copied}
                onCheckedChange={(v) => setCopied(v === true)}
              />
              <Label htmlFor={confirmId}>I've copied it</Label>
            </div>
            <DialogFooter>
              <Button disabled={!copied} onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <DialogHeader>
              <DialogTitle>Create an ingest key</DialogTitle>
              <DialogDescription>
                For {workerName(de.display_name)} on {de.seat?.title ?? "its seat"}. The key is
                shown once.
              </DialogDescription>
            </DialogHeader>
            <FieldSet>
              <FieldLegend variant="label">Scopes</FieldLegend>
              {SCOPES.map(({ scope, label }) => (
                <ScopeBox
                  key={scope}
                  label={label}
                  checked={scopes.includes(scope)}
                  onChange={(on) =>
                    setScopes((prev) =>
                      on ? [...new Set([...prev, scope])] : prev.filter((s) => s !== scope),
                    )
                  }
                />
              ))}
            </FieldSet>
            {create.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {problemText(create.error, "The key could not be created.")}
              </p>
            ) : null}
            <DialogFooter>
              <DialogClose
                disabled={create.isPending}
                render={<Button variant="outline" type="button" />}
              >
                Cancel
              </DialogClose>
              <Button type="submit" disabled={!scopes.length || create.isPending}>
                {create.isPending ? "Creating…" : "Create key"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ScopeBox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <Label htmlFor={id}>{label}</Label>
    </div>
  );
}

function RevokeKeyDialog({
  api,
  companyId,
  de,
  agentKey,
  onRevoked,
}: {
  api: Api;
  companyId: string;
  de: DeProfile;
  agentKey: AgentKey;
  onRevoked: () => void;
}) {
  const [open, setOpen] = useState(false);
  const revoke = useMutation({
    mutationFn: () =>
      api.client.digitalEmployees.revokeKey({
        params: { co: companyId, id: de.membership_id, kid: agentKey.id },
      }),
    onSuccess: () => {
      setOpen(false);
      onRevoked();
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="destructive" size="sm" className="max-md:h-11" onClick={() => setOpen(true)}>
        Revoke<span className="sr-only"> key {agentKey.key_prefix}</span>
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke key {agentKey.key_prefix}…?</DialogTitle>
          <DialogDescription>
            {workerName(de.display_name)}'s runtime can no longer post with this key. This cannot be
            undone.
          </DialogDescription>
        </DialogHeader>
        {revoke.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(revoke.error, "The key could not be revoked.")}
          </p>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button variant="destructive" disabled={revoke.isPending} onClick={() => revoke.mutate()}>
            {revoke.isPending ? "Revoking…" : "Revoke key"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
